import React from "react";
import { Link } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import CurriculumAccordion from "../components/CurriculumAccordion";
import { curriculumSections } from "../data/curriculum";

const bySection = (id) => curriculumSections.filter((s) => s.id === id);

/* ------------------------------------------------------------------ */
/* Local building blocks                                                */
/* ------------------------------------------------------------------ */

const SubHead = ({ children }) => (
  <div className="pt-8 md:pt-10">
    <h2 className="font-display text-[#1e2952] text-[26px] md:text-[32px] leading-tight" style={{ fontWeight: 500 }}>
      {children}
    </h2>
    <div className="mt-4 h-px w-16 bg-[#c9a961]"></div>
  </div>
);

/* ================================================================== */
/* Page                                                                 */
/* ================================================================== */

const Academics = () => {
  return (
    <PageLayout>
      {/* --------------------------------------------------------- */}
      {/* CURRICULUM NARRATIVE                                       */}
      {/* --------------------------------------------------------- */}
      <section className="bg-[#fbf7ee]" data-testid="curriculum-narrative">
        <div className="max-w-[880px] mx-auto px-6 md:px-10 lg:px-14 pt-20 md:pt-28 pb-16 md:pb-20">
          {/* Section heading */}
          <div className="text-center mb-8 md:mb-10">
            <div className="font-sans-nav text-[11.5px] tracking-[0.32em] uppercase text-[#c9a961] mb-4">
              Curriculum
            </div>
            <h1 className="font-display text-[#1e2952] text-[34px] md:text-[52px] leading-[1.1]" style={{ fontWeight: 500 }}>
              An Education Ordered Toward Wisdom.
            </h1>
            <div className="mt-7 flex items-center justify-center gap-3">
              <span className="block h-px w-40 bg-[#c9a961]"></span>
              <span className="text-[#c9a961]">†</span>
              <span className="block h-px w-40 bg-[#c9a961]"></span>
            </div>
            <div className="mt-8 flex justify-center">
              <a
                href="/pdfs/angel-of-peace-curriculum-map.pdf"
                download
                data-testid="download-curriculum-map"
                className="inline-flex items-center gap-2 font-sans-nav text-[11px] tracking-[0.28em] uppercase text-[#c9a961] hover:text-[#1e2952] transition-colors whitespace-nowrap"
              >
                Download the Curriculum Map
                <span aria-hidden="true">↓</span>
              </a>
            </div>
          </div>

          {/* Opening prose */}
          <div className="font-serif-body text-[17px] md:text-[18px] leading-[1.9] text-[#3a466e] space-y-6">
            {/* The Classical Liberal Arts */}
            <SubHead>The Classical Liberal Arts</SubHead>
            <p>
              For centuries, the seven liberal arts formed the foundation of education because they are not merely bodies of knowledge, but arts by which the mind itself is trained to reason, to express, and to understand.
            </p>
            <p>
              In the early years, students establish the essential foundations of <em>Grammar</em> and <em>Arithmetic</em>. They learn to read with precision, understand the structure of language, write and spell accurately, develop a rich vocabulary, and become fluent in number and calculation.
            </p>

            <div className="not-italic pt-4">
              <CurriculumAccordion sections={[...bySection("reading"), ...bySection("writing"), ...bySection("arithmetic")]} className="max-w-full" />
            </div>

            {/* Humanities */}
            <SubHead>Humanities</SubHead>
            <p>
              Humanities brings together Sacred Scripture, History, Literature, Geography, Mythology, Poetry, Art and Music through living books and rich discussion. Students first encounter these subjects through story, narration, and attentive listening, with written narration, discussion, and analysis developing gradually as they mature.
            </p>
            <p>
              The Humanities program rotates through each major historical period three times across the entirety of the curriculum, gaining in depth each time the period is revisited so that students meet the same story of civilization with a wider imagination, sharper reading, and greater discernment at every return.
            </p>

            <div className="not-italic pt-4">
              <div className="mt-4 pt-6 border-t border-[#eae1c4] flex justify-center sm:justify-end">
                <a
                  href="/pdfs/angel-of-peace-humanities-booklist.pdf?v=3"
                  download
                  data-testid="download-booklist-link"
                  className="inline-flex items-center gap-2 font-sans-nav text-[11px] tracking-[0.28em] uppercase text-[#c9a961] hover:text-[#1e2952] transition-colors whitespace-nowrap"
                >
                  View the Humanities Booklist
                  <span aria-hidden="true">↓</span>
                </a>
              </div>
            </div>

            {/* Latin */}
            <SubHead>Latin</SubHead>
            <p>
              Latin begins in the earliest years through stories, prayer, chant, vocabulary, and oral familiarity. Young students become accustomed to hearing and using the language before being asked to analyze it formally.
            </p>
            <p>
              As they mature, students undertake systematic study of vocabulary, grammar, declensions, conjugations, syntax, and translation. The progression becomes increasingly demanding through middle school and prepares students eventually to read Latin texts with greater independence.
            </p>
            <p>
              Latin also has a life beyond the classroom at Angel of Peace. Students encounter it in the Little Office of the Blessed Virgin Mary, sacred music, and the Holy Mass, connecting the language they study academically to the liturgical and intellectual inheritance of the Church.
            </p>

            <div className="not-italic pt-4">
              <div className="font-sans-nav text-[10.5px] tracking-[0.3em] uppercase text-[#c9a961] mb-2">
                Curriculum Resources
              </div>
              <ul className="font-display italic text-[#1e2952] text-[18px] md:text-[20px] leading-snug space-y-1 list-none" style={{ fontWeight: 500 }}>
                <li className="flex items-start gap-2"><span className="text-[#c9a961] not-italic">·</span><span>University of Dallas — Latin Through Stories</span></li>
                <li className="flex items-start gap-2"><span className="text-[#c9a961] not-italic">·</span><span>Memoria Press Forms I–IV</span></li>
                <li className="flex items-start gap-2"><span className="text-[#c9a961] not-italic">·</span><span>Henle I</span></li>
              </ul>
            </div>

            {/* Science */}
            <SubHead>Science</SubHead>
            <p className="font-display italic text-[#1e2952] text-[18px] md:text-[26px] leading-[1.55]" style={{ fontWeight: 500 }}>
              Science begins in wonder and careful attention to creation.
            </p>
            <p>
              In the Lower School, students spend significant time observing the natural world through nature study, field observation, journaling, classification, drawing, measurement, and experimentation. They encounter plants, animals, weather, earth and space, matter, energy, and the physical world while developing habits of careful observation and inquiry.
            </p>
            <p>
              As students mature, science becomes increasingly systematic. They move from direct observation toward measurement, experimentation, scientific reasoning, and formal study of the physical and natural sciences. By middle school, students are developing the knowledge and habits necessary for the disciplined laboratory sciences of high school.
            </p>

            <div className="not-italic pt-4 space-y-8">
              <div>
                <div className="font-sans-nav text-[10.5px] tracking-[0.3em] uppercase text-[#c9a961] mb-2">
                  Kindergarten – Grade 5 · Lower School
                </div>
                <div className="font-display italic text-[#1e2952] text-[18px] md:text-[20px]" style={{ fontWeight: 500 }}>
                  Diocese of Marquette — Catholic Elementary Science curriculum
                </div>
              </div>
              <div>
                <div className="font-sans-nav text-[10.5px] tracking-[0.3em] uppercase text-[#c9a961] mb-2">
                  Grades 6 – 8 · Middle School
                </div>
                <div className="font-display italic text-[#1e2952] text-[18px] md:text-[20px]" style={{ fontWeight: 500 }}>
                  Novare Science: Life Science, Physical Science, and Earth Science
                </div>
              </div>
            </div>

            {/* Section divider */}
            <div className="mt-14 md:mt-16 flex items-center justify-center gap-3">
              <span className="block h-px w-40 bg-[#c9a961]"></span>
              <span className="text-[#c9a961]">†</span>
              <span className="block h-px w-40 bg-[#c9a961]"></span>
            </div>

            {/* A Course of Study That Grows With the Student */}
            <SubHead>A Course of Study That Grows With the Student</SubHead>
            <p>
              The curriculum is intentionally progressive. Students are neither rushed into abstraction before possessing the necessary foundations nor held back from advancing when they demonstrate readiness. Placement in sequential subjects follows each student's demonstrated mastery — read more about our approach on the{" "}
              <Link
                to="/learning-placement"
                data-testid="academics-link-learning-placement"
                className="text-[#c9a961] hover:text-[#1e2952] underline decoration-[#c9a961]/60 underline-offset-2 transition-colors"
              >
                Learning &amp; Placement
              </Link>{" "}
              page.
            </p>

            <p className="font-serif-body italic text-[15px] leading-[1.75] text-[#8a7a4e] pt-6 border-t border-dotted border-[#c9a961]/40 mt-8">
              Curriculum materials will continually be evaluated and are subject to change as the Academy develops.
            </p>
          </div>
        </div>
      </section>

      {/* Explore Electives & Faith link box */}
      <section className="bg-[#fbf7ee]">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 lg:px-14 pt-2 pb-14 text-center">
          <Link
            to="/electives"
            data-testid="academics-cta-electives"
            className="inline-block font-sans-nav text-[12px] tracking-[0.28em] uppercase px-10 py-4 border border-[#c9a961] text-[#1e2952] hover:bg-[#c9a961] hover:text-[#fbf7ee] transition-colors"
          >
            Explore Electives &amp; Faith
          </Link>
        </div>
      </section>

      {/* --------------------------------------------------------- */}
      {/* CTA                                                        */}
      {/* --------------------------------------------------------- */}
      <section className="bg-[#1e2952] text-[#fbf7ee]">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 lg:px-14 py-20 text-center">
          <h2 className="font-display text-[28px] md:text-[38px] leading-tight" style={{ fontWeight: 500 }}>
            Discover Whether Angel of Peace Is Right for Your Family.
          </h2>
          <div className="mt-6 flex items-center justify-center gap-3">
            <span className="block h-px w-40 bg-[#c9a961]"></span>
            <span className="text-[#c9a961]">†</span>
            <span className="block h-px w-40 bg-[#c9a961]"></span>
          </div>
          <div className="mt-10 flex justify-center">
            <Link
              to="/inquire"
              data-testid="academics-cta-inquire"
              className="inline-block font-sans-nav text-[12px] tracking-[0.28em] uppercase px-10 py-4 bg-[#c9a961] text-[#1e2952] hover:bg-[#e2c98a] transition-colors"
            >
              Inquire Today
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Academics;
